import { FakedCodelists } from '@administration/codelists/codelists.interface';
import { Codelists } from '@administration/codelists/codelists.service';
import { inject, Injector, Pipe, PipeTransform } from '@angular/core';
import { QueryService } from '@common/services/query.service';
import { mapResultData, takeUntilResultFinalize } from '@ngneat/query';
import { map, of } from 'rxjs';

@Pipe({
    name: 'codelist'
})
export class CodelistPipe implements PipeTransform {
    private queryService = inject(QueryService);
    injector = inject(Injector);

    /**
     * Transforms a given codelist item ID to its corresponding display text.
     *
     * @param id The id of the codelist item to be transformed (can be an array).
     * @param codelistName The name of the codelist from which to fetch the display value.
     * @param prefixItemWithId (Optional) Whether to prefix the display value with the item's code or ID. Defaults to true.
     * @returns An observable that emits the transformed display text, or a default value in case of error or if not found.
     *
     * @example
     * {{ row[column.field] | codelist: column.codelistName : false | async }}
     */
    transform(
        id: string | number | Array<string | number>,
        codelistName: Codelists | FakedCodelists,
        prefixItemWithId: boolean = false
    ) {
        if (!codelistName || typeof codelistName !== 'string') return of(null);
        if (!id || (Array.isArray(id) && id.length === 0)) return of('-');

        const ids = Array.isArray(id) ? id : [id];

        const codelistQuery$ = this.queryService.getCodelistQuery<any>({
            name: codelistName,
            selectedIds: ids,
            take: ids.length,
            filter: null,
            injector: this.injector
        }).result$;

        return codelistQuery$.pipe(
            mapResultData((data) => {
                // Single id
                if (!Array.isArray(id)) {
                    return this.getText(data?.[0], id, codelistName, prefixItemWithId);
                }

                // Array of ids
                const texts = id.map((singleId) => {
                    const item = data?.find((d) => d.id == singleId || d.code == singleId);
                    return this.getText(item, singleId, codelistName, prefixItemWithId);
                });

                return texts.join(', ');
            }),
            takeUntilResultFinalize(),
            map((result) => result.data)
        );
    }

    private getText(item: any, id: string | number, codelist: string, prefixItemWithId: boolean): string {
        if (!item) {
            console.error(`${id} not found in codelist ${codelist}`);
            return '-';
        }

        const itemPrefix = prefixItemWithId ? `${item.code || item.id} - ` : '';
        return item.customText || `${itemPrefix}${item.name}`;
    }
}
